/**
 * Shop Collapsible Web Component
 * -------------------------------
 * A generic expandable text section with accessible toggle.
 * Renders in Light DOM for SEO indexability.
 *
 * Attributes:
 * - label: Section heading (e.g. "Restrictions")
 * - preview-text: Truncated text shown by default
 * - full-text: Complete text (shown on expand) — optional; omit if text fits in preview.
 *              Ignored when modal-id is set.
 * - button-label: Toggle button text (default "Read more")
 * - button-label-expanded: Expanded state text (default "Read less").
 *                          Unused when modal-id is set.
 * - modal-id: When set, the toggle button opens the <fragno-modal> with this id
 *             instead of expanding the full-text inline. Use for rich/long content.
 *             Opens by setting the `open` attribute on the modal element.
 *
 * Toggle visibility (both inline-expand and modal modes): the toggle is shown
 * only when the preview/content actually overflows its box — measured client-side
 * after fonts load and on resize — and hidden (via the `collapsible__toggle--hidden`
 * class) when the text fits. Callers may render the toggle unconditionally; the
 * component decides visibility. Because visibility keys on *visual overflow*, an
 * inline caller MUST clip .collapsible__preview / .collapsible__content via context
 * CSS (as the store bento box does): a short, non-overflowing preview paired with a
 * longer, distinct full-text would leave the toggle hidden and the full-text
 * unreachable. Once expanded inline, the toggle stays visible so the user can
 * collapse back.
 *
 * Example (inline expand — preview is clipped by context CSS so full-text overflows):
 *   <shop-collapsible
 *     label="Restrictions"
 *     preview-text="10% discount offer valid online at pandora.net. Limit one..."
 *     full-text="10% discount offer valid online at pandora.net. Limit one per customer. Exclusions apply."
 *     button-label="Read more"
 *     button-label-expanded="Read less">
 *   </shop-collapsible>
 *
 * Example (modal):
 *   <shop-collapsible
 *     label="Restrictions"
 *     preview-text="10% discount offer valid online..."
 *     modal-id="restrictions-modal"
 *     button-label="See more">
 *   </shop-collapsible>
 *   <fragno-modal id="restrictions-modal" ...>...</fragno-modal>
 */

import { escapeHTML } from "../../../shared/escape_html.js";

const ShopCollapsible = (function () {
  const TAG_NAME = "shop-collapsible";

  let uidCounter = 0;

  class ShopCollapsible extends HTMLElement {
    static get observedAttributes() {
      return [
        "label",
        "preview-text",
        "full-text",
        "button-label",
        "button-label-expanded",
        "modal-id",
      ];
    }

    connectedCallback() {
      const firstInit = !this._initialized;
      if (firstInit) {
        this._uid = ++uidCounter;
        this.render();
        this._initialized = true;
      }
      // Re-bind the listener and re-observe on every connect. A re-parent (a
      // manual move) fires disconnect → connect, and disconnectedCallback
      // tears both down — first-init-only wiring would leave the toggle dead and the
      // gate unresponsive after the move.
      this._bindToggle();
      // Auto-expand (hash deep-link) must run BEFORE the overflow gate. The gate's
      // initial visibility pass reads `_expanded`, so expanding first means an
      // auto-expanded toggle is measured as expanded and kept visible. Running the
      // gate first would hide a non-overflowing toggle, then auto-expand would reveal
      // the full text but leave "See less" display:none until the next
      // ResizeObserver/fonts.ready fire — a one-frame trapped state on short,
      // hash-linked restrictions.
      if (firstInit && this.id && window.location.hash === `#${this.id}`) {
        this._toggleListener && this._toggleListener();
      }
      this._setupOverflowGate();
    }

    attributeChangedCallback(_name, oldValue, newValue) {
      if (oldValue === newValue || !this._initialized) return;
      this.render();
      this._bindToggle();
      this._setupOverflowGate();
    }

    render() {
      // Track expanded state privately so overflow-gate control flow keys off our own
      // boolean, not the externally-writable aria-expanded attribute. render() rebuilds
      // the toggle in its collapsed state, so reset here to match the fresh DOM.
      this._expanded = false;
      const label = escapeHTML(this.getAttribute("label") || "");
      const previewText = escapeHTML(this.getAttribute("preview-text") || "");
      const fullText = escapeHTML(this.getAttribute("full-text") || "");
      const modalId = this.getAttribute("modal-id") || "";
      const buttonLabel = escapeHTML(
        this.getAttribute("button-label") || "Read more",
      );

      const previewId = `collapsible-preview-${this._uid}`;
      const fullId = `collapsible-full-${this._uid}`;

      // When modal-id is set, the toggle opens a modal — no inline expansion.
      const hasToggle = modalId ? true : !!fullText;
      // The toggle is gated on real overflow, measured right after render. Start it
      // hidden in both modes so a fitting (non-overflowing) preview never flashes the
      // button before the gate runs.
      const toggleHtml = hasToggle
        ? `<fragno-button class="collapsible__toggle collapsible__toggle--hidden"
            hierarchy="secondary"
            size="md"
            label="${buttonLabel}"
            ${!modalId ? `aria-expanded="false" aria-controls="${fullId}"` : ""}>
          </fragno-button>`
        : "";

      const fullTextHtml =
        !modalId && fullText
          ? `<p class="collapsible__full" id="${fullId}" hidden aria-hidden="true">${fullText}</p>`
          : "";

      this.innerHTML = `
        <div class="collapsible">
          <p class="collapsible__label">${label}</p>
          <div class="collapsible__content">
            <p class="collapsible__preview" id="${previewId}">${previewText}</p>
            ${fullTextHtml}
          </div>
          ${toggleHtml}
        </div>
      `;
    }

    disconnectedCallback() {
      this._removeToggleListener();
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    }

    _updateToggleVisibility() {
      const toggle = this.querySelector(".collapsible__toggle");
      if (!toggle) return;
      // Once expanded inline, keep the toggle visible so the user can collapse back.
      // A ResizeObserver/fonts.ready re-fire must never hide the "See less" control.
      // Keyed off our private `_expanded` flag (single source of truth) rather than the
      // externally-writable aria-expanded attribute — an external write to that attribute
      // must not bypass the gate. Modal mode never expands, so `_expanded` stays false
      // and this guard is inert there.
      if (this._expanded) {
        toggle.classList.remove("collapsible__toggle--hidden");
        return;
      }
      const preview = this.querySelector(".collapsible__preview");
      const content = this.querySelector(".collapsible__content");
      // >1 (not >0) tolerates sub-pixel rounding across browsers
      const overflows = (el) => el && el.scrollHeight - el.clientHeight > 1;
      toggle.classList.toggle(
        "collapsible__toggle--hidden",
        !overflows(preview) && !overflows(content),
      );
    }

    _setupOverflowGate() {
      // Idempotent: attributeChangedCallback re-renders and calls this while still
      // connected (no disconnectedCallback in between), so tear down the prior
      // observer, which is bound to the replaced content node.
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
      // No toggle (inline mode with no full-text) → nothing to gate; the teardown
      // above still runs, so a toggle→no-toggle re-render releases its observer.
      if (!this.querySelector(".collapsible__toggle")) return;

      this._updateToggleVisibility();
      if (document.fonts) {
        document.fonts.ready.then(() => {
          if (this.isConnected) this._updateToggleVisibility();
        });
      }
      const content = this.querySelector(".collapsible__content");
      if (content && typeof ResizeObserver !== "undefined") {
        this._resizeObserver = new ResizeObserver(() =>
          this._updateToggleVisibility(),
        );
        this._resizeObserver.observe(content);
      }
    }

    _removeToggleListener() {
      const toggle = this.querySelector(".collapsible__toggle");
      if (toggle && this._toggleListener) {
        toggle.removeEventListener("click", this._toggleListener);
        this._toggleListener = null;
      }
    }

    _bindToggle() {
      this._removeToggleListener();

      const toggle = this.querySelector(".collapsible__toggle");
      if (!toggle) return;

      const modalId = this.getAttribute("modal-id");

      if (modalId) {
        this._toggleListener = () => {
          const modal = document.getElementById(modalId);
          if (modal) {
            modal.setAttribute("open", "");
          }
        };
        toggle.addEventListener("click", this._toggleListener);
        return;
      }

      // Not escaped: these go straight to toggle.setAttribute below, which never
      // parses HTML, so escaping here would only leave literal "&#39;" etc. in the
      // rendered label. escapeHTML is for the innerHTML template in render() above.
      const buttonLabel = this.getAttribute("button-label") || "Read more";
      const buttonLabelExpanded =
        this.getAttribute("button-label-expanded") || "Read less";

      this._toggleListener = () => {
        // `_expanded` is the source of truth; aria-expanded is written below as an
        // accessibility *output*, never read back as control-flow input.
        this._expanded = !this._expanded;
        const preview = this.querySelector(".collapsible__preview");
        const full = this.querySelector(".collapsible__full");

        if (this._expanded) {
          toggle.setAttribute("aria-expanded", "true");
          toggle.setAttribute("label", buttonLabelExpanded);
          if (preview) {
            preview.hidden = true;
            preview.setAttribute("aria-hidden", "true");
          }
          if (full) {
            full.hidden = false;
            full.removeAttribute("aria-hidden");
          }
        } else {
          toggle.setAttribute("aria-expanded", "false");
          toggle.setAttribute("label", buttonLabel);
          if (preview) {
            preview.hidden = false;
            preview.removeAttribute("aria-hidden");
          }
          if (full) {
            full.hidden = true;
            full.setAttribute("aria-hidden", "true");
          }
        }
      };

      toggle.addEventListener("click", this._toggleListener);
    }
  }

  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, ShopCollapsible);
  }

  return ShopCollapsible;
})();

export { ShopCollapsible };
